import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Paprika } from '../../../styles/theme';
import type { ConfigPhase } from '../../../redux/mealPlanUISlice';
import { useTranslation } from 'react-i18next';

/**
 * The bar is `position: absolute; bottom: 0`, so it floats over whatever fills
 * the screen below it. Anything that must stay visible — the scroll content's
 * bottom padding, the cell popover's placement — has to reserve this much room.
 */
export const CONFIG_ACTION_BAR_HEIGHT = 80;

interface Props {
  phase: ConfigPhase;
  selCount: number;
  recipeCount: number;
  onGenerate: () => void;
  onRegenerate: () => void;
  /** Leave the planner: aborts a running generation, or backs out of the settings. */
  onCancel: () => void;
}

export function ConfigActionBar({ phase, selCount, recipeCount, onGenerate, onRegenerate, onCancel }: Props) {
  const { t } = useTranslation();
  // The whole reveal counts as "generating": the cascade commits nothing until
  // its last step, so the primary button stays out of the way until then.
  const isGenerating = phase === 'thinking' || phase === 'cascade';
  const recipesLoading = recipeCount === 0;
  const canGenerate = selCount > 0 && !isGenerating && !recipesLoading;

  return (
    <View style={styles.bar}>
      <Text style={styles.hintText}>
        {isGenerating
          ? t('mealConfig.generating')
          : recipesLoading
          ? t('mealConfig.loadingRecipes')
          : selCount === 0
          ? t('mealConfig.tapToChoose')
          : t('mealConfig.mealSelected', { count: selCount })}
      </Text>
      <Pressable style={styles.cancelBtn} onPress={onCancel} testID="config-cancel">
        <Text style={styles.cancelBtnText}>{t('common.cancel')}</Text>
      </Pressable>
      <Pressable
        style={[styles.generateBtn, !canGenerate && styles.generateBtnDisabled]}
        onPress={canGenerate ? onGenerate : undefined}
      >
        <Ionicons
          name="sparkles-outline"
          size={16}
          color={canGenerate ? '#fff' : Paprika.textMuted}
          style={styles.generateIcon}
        />
        <Text style={[styles.generateBtnText, !canGenerate && styles.generateBtnTextDisabled]}>
          {t('thisWeek.generateMyWeek')}
        </Text>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  bar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: Paprika.surfaceLowest,
    borderTopWidth: 1,
    borderTopColor: Paprika.border,
    paddingHorizontal: 24,
    paddingVertical: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  } as any,
  hintText: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textMuted,
    flex: 1,
  },
  cancelBtn: {
    borderRadius: 100,
    borderWidth: 1.5,
    borderColor: Paprika.border,
    paddingHorizontal: 20,
    paddingVertical: 11.5,
  },
  cancelBtnText: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    color: Paprika.textSecondary,
  },
  generateBtn: {
    backgroundColor: Paprika.primary,
    borderRadius: 100,
    paddingHorizontal: 22,
    paddingVertical: 13,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  generateBtnDisabled: {
    backgroundColor: Paprika.border,
  },
  generateIcon: {
    // size defined inline
  },
  generateBtnText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    color: '#fff',
  },
  generateBtnTextDisabled: {
    color: Paprika.textMuted,
  },
});
